import { Eye, EyeOff } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AuthCardLayout } from "../components/AuthCardLayout";
import { betterAuthRequest } from "../lib/auth";
import { appendLoginStatus, buildForgotPasswordPath } from "../lib/invitation-flow";

export function ResetPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const token = searchParams.get("token");
  const errorFromUrl = searchParams.get("error");
  const redirectPath = searchParams.get("redirect") || "/login";
  const inviteEmail = searchParams.get("email")?.trim().toLowerCase() ?? "";
  const forgotPasswordPath = useMemo(
    () =>
      buildForgotPasswordPath({
        redirectPath,
        email: inviteEmail,
      }),
    [inviteEmail, redirectPath],
  );

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (errorFromUrl?.toUpperCase() === "INVALID_TOKEN") {
      setError("This password reset link is invalid or has expired. Request a new one.");
      return;
    }
    if (!token && !errorFromUrl) {
      setError("Missing reset token. Open the link from your email or request a new one.");
      return;
    }
    setError(null);
  }, [errorFromUrl, token]);

  const passwordsMismatch =
    password.length > 0 && confirmPassword.length > 0 && password !== confirmPassword;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!token) {
      setError("Missing reset token. Open the link from your email or request a new one.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await betterAuthRequest("/reset-password", {
        method: "POST",
        body: JSON.stringify({
          newPassword: password,
          token,
        }),
      });

      navigate(appendLoginStatus(redirectPath, "reset"), { replace: true });
      return;
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Unable to reset password",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthCardLayout
      heroEyebrow="Account recovery"
      heroTitle="Choose a new Datascope password."
      heroDescription="Pick a strong password you have not used before. After updating, you will be redirected to sign in with your new password."
      formEyebrow="Recovery"
      formTitle="Reset password"
      formDescription="Enter a new password for your account."
    >
      <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
        <label className="block">
          <span className="mb-2 block text-sm font-medium text-slate-700">
            New password
          </span>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              minLength={8}
              disabled={submitting || !token}
              onChange={(event) => setPassword(event.target.value)}
              className="block w-full rounded-md border border-slate-200 bg-white px-4 py-3 pr-12 text-sm text-slate-900 shadow-sm outline-none transition focus:border-primary"
              placeholder="Choose a new password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((current) => !current)}
              className="absolute inset-y-0 right-0 flex items-center px-4 text-slate-400 transition hover:text-slate-600"
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
        </label>

        <label className="block">
          <span className="mb-2 block text-sm font-medium text-slate-700">
            Confirm new password
          </span>
          <input
            type={showPassword ? "text" : "password"}
            value={confirmPassword}
            minLength={8}
            disabled={submitting || !token}
            onChange={(event) => setConfirmPassword(event.target.value)}
            aria-invalid={passwordsMismatch}
            className="block w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-primary aria-[invalid=true]:border-red-300"
            placeholder="Re-enter your new password"
            required
          />
          {passwordsMismatch ? (
            <span className="mt-2 block text-xs text-red-600">
              Passwords do not match.
            </span>
          ) : null}
        </label>

        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={submitting || !token || passwordsMismatch}
          className="flex w-full items-center justify-center rounded-md bg-primary px-4 py-3 text-sm font-medium text-white transition hover:bg-[#005f8a] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? "Updating password..." : "Update password"}
        </button>

        <div className="flex items-center justify-between text-sm text-slate-600">
          <Link
            to={forgotPasswordPath}
            className="font-medium text-slate-900 underline-offset-4 hover:underline"
          >
            Request a new link
          </Link>
          <Link
            to={redirectPath}
            className="font-medium text-slate-900 underline-offset-4 hover:underline"
          >
            Back to sign in
          </Link>
        </div>
      </form>
    </AuthCardLayout>
  );
}
